// The writers who lost a contact's contest, behind a toggle, fetched only when it is opened.
//
// The queue read sends every contest without the writers' emails or the buyer's per-writer notes:
// they are most of a queue row's size, and reading them for hundreds of rows is what ran the web
// service out of memory on 12 Oct. Opening the toggle reads this account's contests in full.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAutoSendContest } from '../api/client';
import type { ContactContest } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { losingWriters } from '../lib/sends';
import { StepView } from './StepView';

export function LosingSequences({
  accountId,
  contactKey,
  contest,
}: {
  accountId: string;
  contactKey: string;
  contest: ContactContest;
}) {
  const { getToken } = useAuthContext();
  const [open, setOpen] = useState(false);
  // One read per account, shared by its contacts' toggles, and kept while the page is open.
  const detail = useQuery({
    queryKey: ['auto-send-contest', accountId],
    queryFn: () => fetchAutoSendContest(getToken, accountId),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  // The personas are in the trimmed contest, so the count shows before anything is fetched.
  const loserCount = losingWriters(contest).length;
  if (!loserCount) return null;
  const full = detail.data?.contacts.find(contact => contact.key === contactKey)?.contest ?? null;

  return (
    <details className="sendlosers" onToggle={event => setOpen(event.currentTarget.open)}>
      <summary className="sendquiet">Losing sequences ({loserCount})</summary>
      {open && detail.isPending ? <p className="sendquiet">Loading the losing sequences…</p> : null}
      {detail.error ? <p className="sendnote bad">Could not load the losing sequences.</p> : null}
      {full ? losingWriters(full).map(writer => (
        <div key={writer.persona} className="sendloser">
          <div className="sendstephead">
            <b>{writer.persona}</b> · {full.buyer.scores[writer.persona] ?? '–'}/10
            {full.buyer.reasons[writer.persona] ? ` — ${full.buyer.reasons[writer.persona]}` : ''}
          </div>
          {full.buyer.delete_lines[writer.persona] ? (
            <p className="sendquiet sendwhy">Would delete: “{full.buyer.delete_lines[writer.persona]}”</p>
          ) : null}
          {writer.steps.map((step, index) => <StepView key={index} step={step} index={index} />)}
        </div>
      )) : null}
    </details>
  );
}
